import { useLanguage } from '../context/LanguageContext';
import { Check, Shield, Truck, Warehouse, Gift, ArrowRight, Heart, Sparkles, ClipboardList } from 'lucide-react';

const PARTNER_LOGOS = "/assets/partner_logos.png";

export default function PricingSection({ onSubscribeClick }) {
  const { t, isRomanian, getBasePrice, getUpgradePrice } = useLanguage();

  const included = [
    { text: isRomanian ? '5-7 jucării și recompense premium' : '5-7 premium toys & treats' },
    { text: isRomanian ? 'Personalizat pe baza chestionarului' : 'Personalized based on questionnaire' },
    { text: isRomanian ? 'Livrare în 2 zile lucrătoare' : '2-day delivery' },
    { text: isRomanian ? 'Anulezi oricând, fără penalități' : 'Cancel anytime, no penalties' },
  ];

  return (
    <section id="pricing" className="py-12 md:py-16 bg-brand-cream relative overflow-hidden">
      <div className="absolute top-0 right-0 w-72 h-72 bg-brand-orange/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

      <div className="section-container relative">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="sticker-badge mb-3">
              <Gift className="w-4 h-4 inline mr-1" />
              {isRomanian ? 'Abonament simplu' : 'Simple subscription'}
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-brown mt-4 mb-3">
              {isRomanian ? 'O cutie, o surpriză' : 'One box, one surprise'}
            </h2>
            <p className="text-brand-muted text-base max-w-xl mx-auto">
              {isRomanian
                ? 'Fără planuri complicate. Un singur abonament lunar, personalizat pentru prietenul tău blănos.'
                : 'No complicated plans. One simple monthly subscription, personalized for your furry friend.'}
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-brand-border/50 overflow-hidden">
            <div className="grid md:grid-cols-5">
              {/* Left: Price + CTA — ORANGE */}
              <div className="md:col-span-2 bg-brand-orange p-8 flex flex-col justify-center text-white">
                <p className="text-white/70 text-sm uppercase tracking-widest mb-1">
                  {isRomanian ? 'Lunar' : 'Monthly'}
                </p>
                {/* Strikethrough old price */}
                <p className="text-white/50 text-lg line-through mb-0.5">
                  {isRomanian ? '150 lei' : '\u20AC30'}
                </p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-heading text-6xl font-bold">
                    {getBasePrice()}
                  </span>
                  <div className="text-left">
                    <span className="text-2xl text-white/80">{isRomanian ? 'lei' : '\u20AC'}</span>
                    <p className="text-sm text-white/60">{isRomanian ? '/lună' : '/month'}</p>
                  </div>
                </div>

                <p className="text-white/60 text-sm mb-5">
                  {isRomanian
                    ? `Upgrade: +${getUpgradePrice()} lei/lună`
                    : `Upgrade: +\u20AC${getUpgradePrice()}/month`}
                </p>

                <button
                  onClick={onSubscribeClick}
                  className="w-full bg-white text-brand-orange rounded-full px-6 py-4 font-bold text-lg 
                             hover:bg-white/90 transition-all duration-200 flex items-center justify-center gap-2
                             shadow-[0_4px_20px_rgba(255,255,255,0.4)] hover:shadow-[0_6px_28px_rgba(255,255,255,0.6)] hover:-translate-y-0.5
                             ring-2 ring-white/30"
                  data-testid="pricing-subscribe-btn"
                >
                  {t('pricing_subscribe')}
                  <ArrowRight className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2 mt-3 text-white/50 text-xs justify-center">
                  <Shield className="w-3.5 h-3.5" />
                  <span>{t('pricing_cancel')}</span>
                </div>

                {/* White paw icon */}
                <div className="flex justify-center mt-4">
                  <svg className="w-8 h-8 text-white/30" fill="currentColor" viewBox="0 0 512 512"><path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5.3-86.2 32.6-96.8 70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7.9 78.6 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2-25.8 0-46.7-20.9-46.7-46.7v-1.6c0-10.4 1.6-20.8 5.2-30.5zM324.7 89.4c14.3-42.9 51.7-66.1 83.6-51.5 32 14.6 45.9 57.9 31.6 100.7-14.3 42.9-51.7 66.1-83.6 51.5-32-14.6-45.9-57.9-31.6-100.7zm89.1 164.5c24.5 14 29.1 51.7 10.2 84.1-18.9 32.4-54 46.1-78.5 32.1s-29.1-51.7-10.2-84.1c18.9-32.4 54-46.1 78.5-32.1z"/></svg>
                </div>
              </div>

              {/* Right: What's included */}
              <div className="md:col-span-3 p-8">
                <div className="flex items-center gap-2 mb-5">
                  <Sparkles className="w-5 h-5 text-brand-orange" />
                  <h3 className="font-heading text-xl font-bold text-brand-brown">
                    {isRomanian ? 'Ce primești lunar' : 'What you get every month'}
                  </h3>
                </div>

                <ul className="space-y-3 mb-6">
                  {included.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-brand-success/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-brand-success" />
                      </div>
                      <span className="text-brand-brown font-medium">{item.text}</span>
                    </li>
                  ))}
                </ul>

                {/* March promo */}
                <div className="bg-brand-orange/5 border border-brand-orange/20 rounded-2xl p-4 flex items-start gap-3 mb-5">
                  <Heart className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-brand-brown text-sm">
                      {isRomanian ? 'Ofertă vară: Livrare GRATUITĂ pe viață!' : 'Summer offer: FREE delivery for life!'}
                    </p>
                    <p className="text-brand-muted text-xs mt-1">
                      {isRomanian
                        ? 'Abonează-te acum și nu vei plăti niciodată livrarea.'
                        : 'Subscribe now and never pay for delivery again.'}
                    </p>
                  </div>
                </div>

                {/* Trust row */}
                <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-brand-border/50">
                  <div className="flex items-center gap-1.5 text-xs text-brand-muted">
                    <Truck className="w-4 h-4 text-brand-orange" />
                    {t('trust_shipping')}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-brand-muted">
                    <Warehouse className="w-4 h-4 text-brand-brown" />
                    {'\u{1F1F7}\u{1F1F4}'} {'\u{1F1E7}\u{1F1EC}'}
                  </div>
                  <div className="flex-1" />
                  <div className="flex items-center gap-2">
                    <img src={PARTNER_LOGOS} alt="DPD Romania, Visa, Mastercard" className="h-8 object-contain" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Three Simple Steps */}
          <div className="mt-12" id="how-it-works">
            <div className="text-center mb-8">
              <span className="sticker-badge mb-3">
                <ClipboardList className="w-4 h-4 inline mr-1" />
                {t('how_badge')}
              </span>
              <h3 className="font-heading text-3xl sm:text-4xl font-bold text-brand-brown mt-4 mb-2">
                {t('how_title')}
              </h3>
              <p className="text-brand-muted text-base max-w-xl mx-auto">
                {t('how_subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
              {[
                { icon: ClipboardList, title: t('how_step1_title'), desc: t('how_step1_desc'), color: 'bg-brand-orange' },
                { icon: Truck, title: t('how_step2_title'), desc: t('how_step2_desc'), color: 'bg-brand-brown' },
                { icon: Heart, title: t('how_step3_title'), desc: t('how_step3_desc'), color: 'bg-brand-success' },
              ].map((step, index) => (
                <div key={index} className="relative text-center group">
                  <div className="relative inline-block mb-5">
                    <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform shadow-lg`}>
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-brand-cream rounded-full flex items-center justify-center border-2 border-brand-border font-heading font-bold text-sm text-brand-brown shadow-md">
                      {index + 1}
                    </div>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-brand-brown mb-2">{step.title}</h4>
                  <p className="text-brand-muted text-sm leading-relaxed">{step.desc}</p>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-brand-border/50 -z-10" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
